import React from "react";
import { createPortal } from "react-dom";
import { useTutorial } from "@/hooks/useTutorial";
import { isNativeApp } from "@/lib/runtime";

const AUTO_START_DELAY_MS = 320;

const VIEWPORT_PADDING = 8;
const PANEL_MARGIN = 20;
const PANEL_SPACING = 18;
const MOBILE_MAX_WIDTH = 640;

function isVisibleTutorialTarget(element) {
  if (!element || typeof window === "undefined") return false;
  const style = window.getComputedStyle(element);
  if (
    style.display === "none" ||
    style.visibility === "hidden" ||
    style.opacity === "0"
  ) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function findTutorialTarget(selector) {
  if (typeof document === "undefined" || !selector) return null;
  const candidates = Array.from(document.querySelectorAll(selector));
  return candidates.find(isVisibleTutorialTarget) || candidates[0] || null;
}

function expandRect(rect, padding = { x: 4, y: 4 }, viewportPadding = VIEWPORT_PADDING) {
  if (!rect) return null;
  const width = typeof window !== "undefined" ? window.innerWidth : 0;
  const height = typeof window !== "undefined" ? window.innerHeight : 0;
  const resolvedPadding =
    typeof padding === "number"
      ? { x: padding, y: padding }
      : {
          x: Math.max(padding?.x ?? 16, 0),
          y: Math.max(padding?.y ?? 0, 0),
        };
  const top = Math.max(rect.top - resolvedPadding.y, viewportPadding);
  const left = Math.max(rect.left - resolvedPadding.x, viewportPadding);
  const spotlightWidth = Math.min(
    rect.width + resolvedPadding.x * 2,
    width - left - viewportPadding
  );
  const spotlightHeight = Math.min(
    rect.height + resolvedPadding.y * 2,
    height - top - viewportPadding
  );
  return {
    top,
    left,
    width: Math.max(spotlightWidth, 0),
    height: Math.max(spotlightHeight, 0),
  };
}

function createCircularRect(rect, padding = 0, viewportPadding = VIEWPORT_PADDING) {
  if (!rect) return null;
  const width = typeof window !== "undefined" ? window.innerWidth : 0;
  const height = typeof window !== "undefined" ? window.innerHeight : 0;
  const extra = Math.max(typeof padding === "number" ? padding : padding?.x ?? 0, typeof padding === "number" ? padding : padding?.y ?? 0, 0);
  const size = Math.max(rect.width, rect.height) + extra * 2;
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 2;
  const left = Math.min(
    Math.max(centerX - size / 2, viewportPadding),
    Math.max(width - size - viewportPadding, viewportPadding)
  );
  const top = Math.min(
    Math.max(centerY - size / 2, viewportPadding),
    Math.max(height - size - viewportPadding, viewportPadding)
  );

  return {
    top,
    left,
    width: Math.max(Math.min(size, width - viewportPadding * 2), 0),
    height: Math.max(Math.min(size, height - viewportPadding * 2), 0),
  };
}

function useTargetRect(run, step) {
  const [rect, setRect] = React.useState(null);

  const updateRect = React.useCallback(() => {
    if (!run || !step?.target) {
      setRect(null);
      return;
    }
    if (typeof document === "undefined") return;

    if (step.target === "body") {
      setRect({
        top: VIEWPORT_PADDING,
        left: VIEWPORT_PADDING,
        width: window.innerWidth - VIEWPORT_PADDING * 2,
        height: window.innerHeight - VIEWPORT_PADDING * 2,
      });
      return;
    }

    const element = findTutorialTarget(step.target);
    if (!element) {
      setRect(null);
      return;
    }

    const elementRect = element.getBoundingClientRect();
    setRect({
      top: elementRect.top,
      left: elementRect.left,
      width: elementRect.width,
      height: elementRect.height,
    });
  }, [run, step]);

  React.useEffect(() => {
    if (!run) return;
    updateRect();

    const handleResize = () => updateRect();
    const observer =
      typeof MutationObserver !== "undefined" && typeof document !== "undefined"
        ? new MutationObserver(() => updateRect())
        : null;

    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);
    observer?.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style", "hidden", "aria-hidden"],
    });
    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
      observer?.disconnect();
    };
  }, [run, updateRect]);

  React.useEffect(() => {
    if (!run) {
      setRect(null);
      return;
    }
    updateRect();
  }, [run, step, updateRect]);

  return rect;
}

function rectsOverlap(a, b) {
  if (!a || !b) return false;
  return !(
    a.left + a.width <= b.left ||
    a.left >= b.left + b.width ||
    a.top + a.height <= b.top ||
    a.top >= b.top + b.height
  );
}

function usePanelPosition(run, spotlightRect) {
  const panelRef = React.useRef(null);
  const [panelStyle, setPanelStyle] = React.useState({ opacity: 0 });
  const [placement, setPlacement] = React.useState("floating");

  React.useLayoutEffect(() => {
    if (!run) {
      setPanelStyle({ opacity: 0 });
      return;
    }
    if (typeof window === "undefined") return;
    const panel = panelRef.current;
    if (!panel) return;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isNarrow = viewportWidth <= MOBILE_MAX_WIDTH;
    const margin = PANEL_MARGIN;
    const spacing = PANEL_SPACING;
    const width = panel.offsetWidth;
    const height = panel.offsetHeight;

    let top = (viewportHeight - height) / 2;
    let left = (viewportWidth - width) / 2;
    let nextPlacement = "floating";

    if (spotlightRect) {
      const below = spotlightRect.top + spotlightRect.height + spacing;
      const above = spotlightRect.top - spacing - height;
      const fitsBelow = below + height <= viewportHeight - margin;
      const fitsAbove = above >= margin;

      if (fitsBelow || (!fitsAbove && below + height <= viewportHeight)) {
        top = Math.min(below, viewportHeight - height - margin);
        nextPlacement = "bottom";
      } else if (fitsAbove) {
        top = Math.max(above, margin);
        nextPlacement = "top";
      }

      left = spotlightRect.left + spotlightRect.width / 2 - width / 2;
      const maxLeft = viewportWidth - width - margin;
      left = Math.min(Math.max(left, margin), Math.max(maxLeft, margin));

      const proposedRect = { top, left, width, height };
      if (rectsOverlap(proposedRect, spotlightRect)) {
        const spaceRight = viewportWidth - (spotlightRect.left + spotlightRect.width) - spacing;
        const spaceLeft = spotlightRect.left - spacing;
        const centerTop = spotlightRect.top + spotlightRect.height / 2 - height / 2;
        if (!isNarrow && spaceRight >= width) {
          left = Math.min(
            spotlightRect.left + spotlightRect.width + spacing,
            viewportWidth - width - margin
          );
          top = Math.min(Math.max(centerTop, margin), viewportHeight - height - margin);
          nextPlacement = "right";
        } else if (!isNarrow && spaceLeft >= width) {
          left = Math.max(spotlightRect.left - spacing - width, margin);
          top = Math.min(Math.max(centerTop, margin), viewportHeight - height - margin);
          nextPlacement = "left";
        } else if (fitsAbove) {
          top = Math.max(spotlightRect.top - spacing - height, margin);
          nextPlacement = "top";
        } else if (fitsBelow) {
          top = Math.min(
            spotlightRect.top + spotlightRect.height + spacing,
            viewportHeight - height - margin
          );
          nextPlacement = "bottom";
        } else {
          const preferBottom = spotlightRect.top < viewportHeight / 2;
          if (preferBottom) {
            top = Math.min(
              spotlightRect.top + spotlightRect.height + spacing,
              viewportHeight - height - margin
            );
            nextPlacement = "bottom";
          } else {
            top = Math.max(spotlightRect.top - spacing - height, margin);
            nextPlacement = "top";
          }
        }
      }

      if (isNarrow && nextPlacement === "floating") {
        const preferBottom = spotlightRect.top < viewportHeight / 2;
        if (preferBottom) {
          top = Math.min(
            spotlightRect.top + spotlightRect.height + spacing,
            viewportHeight - height - margin
          );
          nextPlacement = "bottom";
        } else {
          top = Math.max(spotlightRect.top - spacing - height, margin);
          nextPlacement = "top";
        }
        left = Math.min(Math.max(left, margin), viewportWidth - width - margin);
      }
    }

    const arrowSize = 14;
    const spotlightCenterX = spotlightRect
      ? spotlightRect.left + spotlightRect.width / 2
      : viewportWidth / 2;
    const spotlightCenterY = spotlightRect
      ? spotlightRect.top + spotlightRect.height / 2
      : viewportHeight / 2;
    const arrowLeft = Math.min(
      Math.max(spotlightCenterX - left - arrowSize / 2, 18),
      Math.max(width - arrowSize - 18, 18)
    );
    const arrowTop = Math.min(
      Math.max(spotlightCenterY - top - arrowSize / 2, 18),
      Math.max(height - arrowSize - 18, 18)
    );

    setPlacement(nextPlacement);
    setPanelStyle({
      top,
      left,
      opacity: 1,
      "--tutorial-arrow-left": `${arrowLeft}px`,
      "--tutorial-arrow-top": `${arrowTop}px`,
    });
  }, [run, spotlightRect]);

  return { panelRef, panelStyle, placement };
}

function PageTutorialOverlay({
  run,
  steps,
  stepIndex,
  onNext,
  onPrevious,
  onSkip,
}) {
  const step = steps[stepIndex] || steps[steps.length - 1];
  const rect = useTargetRect(run, step);
  const spotlightRect = React.useMemo(
    () =>
      rect
        ? step?.spotlightShape === "circle"
          ? createCircularRect(rect, step?.spotlightPadding, step?.viewportPadding ?? VIEWPORT_PADDING)
          : expandRect(rect, step?.spotlightPadding, step?.viewportPadding ?? VIEWPORT_PADDING)
        : null,
    [rect, step?.spotlightPadding, step?.spotlightShape, step?.viewportPadding]
  );
  const spotlightRadius = step?.spotlightRadius ?? 16;
  const { panelRef, panelStyle, placement } = usePanelPosition(run, spotlightRect);
  const portalTarget = typeof document !== "undefined" ? document.body : null;
  const totalSteps = steps.length;
  const isLast = stepIndex >= totalSteps - 1;
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;
  const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 0;
  const haloSpread = Math.max(viewportWidth, viewportHeight) * 2;

  React.useEffect(() => {
    if (!run || typeof document === "undefined") return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [run]);

  const previousIndexRef = React.useRef(-1);
  React.useEffect(() => {
    if (!run || typeof document === "undefined") return;
    if (previousIndexRef.current === stepIndex) return;
    previousIndexRef.current = stepIndex;
    const currentStep = steps[stepIndex];
    if (!currentStep?.target || currentStep.target === "body") return;
    const element = findTutorialTarget(currentStep.target);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    }
  }, [run, stepIndex, steps]);

  if (!run || !portalTarget || !step) {
    return null;
  }

  return createPortal(
    <div className="page-tutorial-overlay" role="dialog" aria-modal>
      <div className="page-tutorial-overlay__blanket" />
      {spotlightRect ? (
        <div
          className="page-tutorial-overlay__spotlight"
          style={{
            top: spotlightRect.top,
            left: spotlightRect.left,
            width: spotlightRect.width,
            height: spotlightRect.height,
            borderRadius: spotlightRadius,
            boxShadow: `0 0 0 1px rgba(234, 209, 209, 0.65), 0 0 0 ${haloSpread}px rgba(7, 10, 19, 0.78)`,
          }}
        />
      ) : (
        <div className="page-tutorial-overlay__scrim" />
      )}
      <div
        ref={panelRef}
        className={`page-tutorial-overlay__panel page-tutorial-overlay__panel--${placement}`}
        style={panelStyle}
      >
        <div className="page-tutorial-overlay__progress">
          <span className="page-tutorial-overlay__step">
            Steg {Math.min(stepIndex + 1, totalSteps)} av {totalSteps}
          </span>
          <button type="button" className="page-tutorial-overlay__skip" onClick={onSkip}>
            Hoppa över
          </button>
        </div>
        <div className="page-tutorial-overlay__content">
          {step.title && <h2 className="page-tutorial-overlay__title">{step.title}</h2>}
          {step.content && (
            <div className="page-tutorial-overlay__description">
              {typeof step.content === "string" ? <p>{step.content}</p> : step.content}
            </div>
          )}
        </div>
        <div className="page-tutorial-overlay__actions">
          <button
            type="button"
            className="page-tutorial-overlay__secondary"
            onClick={stepIndex === 0 ? onSkip : onPrevious}
          >
            {stepIndex === 0 ? "Stäng" : "Tillbaka"}
          </button>
          <button type="button" className="page-tutorial-overlay__primary" onClick={onNext}>
            {isLast ? "Klart" : "Nästa"}
          </button>
        </div>
      </div>
    </div>,
    portalTarget
  );
}

export default function PageTutorial({ pageName, autoStart = true, onStepChange }) {
  const disabledForNative = isNativeApp();
  const {
    steps,
    run,
    stepIndex,
    shouldAutoStart,
    startTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    completed,
    introCompleted,
  } = useTutorial(pageName);

  React.useEffect(() => {
    if (disabledForNative) return;
    if (typeof onStepChange !== "function") return;
    onStepChange({ run, stepIndex, steps, completed, pageName });
  }, [completed, disabledForNative, onStepChange, pageName, run, stepIndex, steps]);

  React.useEffect(() => {
    if (disabledForNative) return undefined;
    if (!autoStart) return undefined;
    if (!shouldAutoStart || run) return undefined;
    const timer = window.setTimeout(() => {
      startTutorial();
    }, AUTO_START_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [autoStart, disabledForNative, shouldAutoStart, run, startTutorial]);

  if (disabledForNative) return null;
  if (!steps.length) return null;
  if ((completed || !introCompleted) && !run) return null;

  return (
    <PageTutorialOverlay
      run={run}
      steps={steps}
      stepIndex={stepIndex}
      onNext={nextStep}
      onPrevious={previousStep}
      onSkip={skipTutorial}
    />
  );
}
